import authService from '@/api/authService';
import { Alert, Box, Code, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import logo from 'common/assets/Mereko_Logo_Full.png';
import loginButtonImage from 'common/assets/ms-symbollockup_mssymbol_19.svg';
import { useTranslator } from 'translations';
import z from 'zod';

const SearchSchema = z.object({
    redirect: z.string().optional(),
    // Present when the better-auth OAuth provider (MCP interface) redirects here mid-authorize.
    client_id: z.string().optional(),
    sig: z.string().optional(),
    // Set by better-auth when sign-in fails, or by the auth guard when the session lookup failed.
    error: z.string().optional(),
    error_description: z.string().optional(),
});

/**
 * When the login page was entered from an OAuth authorize flow (signed query from the better-auth
 * OAuth provider), the user must return to the authorize endpoint after login to continue it.
 */
function oauthContinueUrl(search: z.infer<typeof SearchSchema>) {
    if (!search.client_id || !search.sig) return undefined;
    return `/api/auth/oauth2/authorize${window.location.search}`;
}

export const Route = createFileRoute('/login')({
    component: RouteComponent,
    validateSearch: SearchSchema,
    async beforeLoad({ search }) {
        const login = await authService.appLogin.get().catch(() => null);

        if (login) {
            const oauthContinue = oauthContinueUrl(search);
            if (oauthContinue) {
                throw redirect({ href: oauthContinue });
            }

            throw redirect({
                to: search.redirect ?? '/',
                search: {
                    redirect: undefined,
                    error: undefined,
                    error_description: undefined,
                },
            });
        }
    },
});

function RouteComponent() {
    const t = useTranslator();
    const search = Route.useSearch();
    const { redirect, error, error_description } = search;
    const oauthContinue = oauthContinueUrl(search);

    return (
        <Box
            css={{
                width: '100%',
                height: '100%',
                padding: '1rem',
                display: 'grid',
                justifyContent: 'center',
                justifyItems: 'center',
                alignContent: 'start',
                gap: '2rem',
            }}
        >
            <img
                css={{
                    width: '100%',
                    maxWidth: '10rem',
                    marginBottom: '4rem',
                }}
                src={logo}
                alt="Mereko Logo"
            />

            {error && (
                <Alert
                    color="red"
                    icon={<IconAlertTriangle />}
                    title={t('login.error.title')}
                    css={{ maxWidth: '30rem' }}
                >
                    <div>
                        {error === 'user_not_registered'
                            ? t('login.error.userNotRegistered')
                            : t('login.error.description')}
                    </div>
                    <Code block css={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>
                        {t('login.error.code', { code: error })}
                        {error_description && `\n${error_description}`}
                    </Code>
                </Alert>
            )}

            <div>{t('login.welcome')}</div>
            <UnstyledButton
                css={{
                    justifySelf: 'center',
                    height: '41px',
                    padding: '0 12px',
                    display: 'grid',
                    gap: '12px',
                    gridAutoFlow: 'column',
                    alignItems: 'center',
                    color: '#ffffff',
                    backgroundColor: '#2f2f2f',
                    fontSize: '15px',
                    fontWeight: 'bold',
                }}
                onClick={() => authService.login(oauthContinue ?? redirect ?? '/')}
            >
                <img src={loginButtonImage} alt="Microsoft Logo" />
                <span>{t('login.button')}</span>
            </UnstyledButton>
        </Box>
    );
}
